/**
 * Where every failed load ends: what went wrong in plain words, and a way to try again. Never an
 * endless skeleton. `detail` is for staff, such as a request id, and sits below in small type.
 * While a retry is under way the button keeps its label and ignores presses. `focusOnShow` moves
 * focus to the message when it appears, for a failure that replaces the control the person pressed.
 */
import * as stylex from "@stylexjs/stylex";
import type { ReactNode } from "react";
import { useEffect, useRef } from "react";
import "@lattice/tokens/space.stylex";
import { space } from "@lattice/tokens/space.stylex";
import { Button } from "./Button.tsx";
import { Heading, Text } from "./Text.tsx";
export interface ErrorStateProps {
  readonly title?: string;
  readonly body?: ReactNode;
  readonly onRetry?: () => void;
  readonly retrying?: boolean;
  readonly detail?: string;
  readonly variant?: "inline" | "page";
  readonly level?: 2 | 3;
  readonly testId?: string;
  readonly focusOnShow?: boolean;
}
export function ErrorState({
  title = "We couldn't load this",
  body = "Try again in a moment. If it keeps happening, contact support.",
  onRetry,
  retrying = false,
  detail,
  variant = "inline",
  level = 2,
  testId,
  focusOnShow = false
}: ErrorStateProps) {
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (focusOnShow) box.current?.focus();
  }, [focusOnShow]);
  const focus = focusOnShow ? {
    ref: box,
    tabIndex: -1
  } : {};
  const retry = onRetry === undefined ? null : <Button variant="secondary" size="md" onClick={onRetry} loading={retrying}>
        Try again
      </Button>;
  const note = detail === undefined ? null : <span className="xj0a0fe">
        <Text variant="caption" tone="tertiary" as="span">
          {detail}
        </Text>
      </span>;
  if (variant === "inline") return <div role="alert" data-testid={testId} {...focus} {...{
    0: {
      className: "x78zum5 x1a02dak x6s0dn4 x626il7"
    },
    1: {
      className: "x78zum5 x1a02dak x6s0dn4 x626il7 x1k57tk5"
    }
  }[!!focusOnShow << 0]}>
        <Text tone="danger">
          {title}. {body}
        </Text>
        {retry}
        {note}
      </div>;
  return <div role="alert" data-testid={testId} {...focus} {...{
    0: {
      className: "x78zum5 xdt5ytf x1cy8zhl x626il7 x12qliqu x13sch6v"
    },
    1: {
      className: "x78zum5 xdt5ytf x1cy8zhl x626il7 x12qliqu x13sch6v x1k57tk5"
    }
  }[!!focusOnShow << 0]}>
      <Heading level={level} size="sectionTitle">
        {title}
      </Heading>
      <Text tone="secondary">{body}</Text>
      {retry}
      {note}
    </div>;
}